import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { CASTOR_API_URL } from '../api-config';
import { AuthService } from '../auth/auth.service';

export interface CastorField {
  fieldId: string;
  type: string;
  label: string;
  orderIndex: number;
  fullWidth: boolean;
  mandatory?: boolean;
  defaultValue?: string | number | boolean | null;
  minValue?: number;
  maxValue?: number;
  listOptions?: string[];
  /** Used in builder when type === 'number': 'integer' | 'decimal'. Not sent to API. */
  numberKind?: 'integer' | 'decimal';
  /** Optional input mask for string fields (e.g. CPF: NNN.NNN.NNN-NN). */
  mask?: string;
  /** Optional display labels for boolean fields. */
  trueLabel?: string;
  falseLabel?: string;
  /** Optional card/group metadata for layout. */
  cardId?: string;
  cardTitle?: string;
}

export interface CastorCrud {
  crudId: string;
  name: string;
  customerId?: string;
  customerName?: string;
  marketplace?: boolean;
  tableName?: string;
  createdAt?: string;
  assignmentCreatedAt?: string;
  fields?: CastorField[];
}

export interface CastorCrudCreate {
  customerId: string;
  name: string;
  fields: Array<{
    fieldId?: string;
    type: string;
    label: string;
    orderIndex: number;
    fullWidth: boolean;
    mandatory?: boolean;
    defaultValue?: string | number | boolean | null;
    minValue?: number;
    maxValue?: number;
    listOptions?: string[];
    mask?: string;
    trueLabel?: string;
    falseLabel?: string;
    cardId?: string;
    cardTitle?: string;
  }>;
}

@Injectable({ providedIn: 'root' })
export class CrudsService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private async headers(): Promise<Record<string, string>> {
    const token = await this.auth.getIdToken();
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = token;
    return h;
  }

  async listCruds(customerId?: string): Promise<CastorCrud[]> {
    const headers = await this.headers();
    let params = new HttpParams();
    if (customerId != null && customerId !== '') {
      params = params.set('customerId', customerId);
    }
    const res = await firstValueFrom(
      this.http.get<{ items: CastorCrud[] }>(`${CASTOR_API_URL}/cruds`, { headers, params })
    );
    return res?.items ?? [];
  }

  async getCrud(crudId: string, customerId?: string): Promise<CastorCrud> {
    const headers = await this.headers();
    const params = customerId ? new HttpParams().set('customerId', customerId) : undefined;
    return firstValueFrom(
      this.http.get<CastorCrud>(`${CASTOR_API_URL}/cruds/${crudId}`, { headers, params })
    );
  }

  async createCrud(body: CastorCrudCreate): Promise<{ crudId: string; name: string }> {
    const headers = await this.headers();
    const res = await firstValueFrom(
      this.http.post<{ ok: boolean; crudId: string; name: string }>(
        `${CASTOR_API_URL}/cruds`,
        body,
        { headers }
      )
    );
    return { crudId: res.crudId, name: res.name };
  }

  async updateCrud(crudId: string, body: { name: string; fields: CastorCrudCreate['fields']; customerId?: string; customerName?: string }): Promise<void> {
    const headers = await this.headers();
    const params = body.customerId ? new HttpParams().set('customerId', body.customerId) : undefined;
    await firstValueFrom(
      this.http.put(`${CASTOR_API_URL}/cruds/${crudId}`, body, { headers, params })
    );
  }

  async deleteCrud(crudId: string, customerId?: string): Promise<void> {
    const headers = await this.headers();
    const params = customerId ? new HttpParams().set('customerId', customerId) : undefined;
    await firstValueFrom(
      this.http.delete(`${CASTOR_API_URL}/cruds/${crudId}`, { headers, params })
    );
  }
}
